import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Typography, TextField, Select, MenuItem, Button,
    InputAdornment, IconButton, Paper, Modal, List, ListItem,
    Tooltip, Divider,
} from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import platformsData from '../data/platforms.json';
import Header from './widgets/Header';

const URLGenerator = () => {
    const { t } = useTranslation();
    const [platforms, setPlatforms] = useState([]);
    const [selectedPlatform, setSelectedPlatform] = useState('');
    const [baseUrl, setBaseUrl] = useState('');
    const [parameters, setParameters] = useState([]);
    const [urlDisplay, setUrlDisplay] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [currentInput, setCurrentInput] = useState(null);

    useEffect(() => {
        setPlatforms(platformsData);
    }, []);

    const handlePlatformChange = (event) => {
        const platformSlug = event.target.value;
        setSelectedPlatform(platformSlug);

        const platform = platforms.find((p) => p.slug === platformSlug);
        setParameters(platform ? platform.parameters || [] : []);
    };

    const handleParameterChange = (index, value) => {
        const updatedParameters = [...parameters];
        updatedParameters[index].value = value;
        setParameters(updatedParameters);
        updateURLDisplay(baseUrl, updatedParameters);
    };

    const updateURLDisplay = (newBaseURL, updatedParameters) => {
        try {
            const queryParams = updatedParameters
                .filter((param) => param.value)
                .map((param) => `${param.name}=${param.value}`)
                .join('&');
            const query = queryParams ? `?${queryParams}` : '';
            setUrlDisplay(newBaseURL + query);
        } catch {
            setUrlDisplay(t('urlBuilder.invalid_url'));
        }
    };

    const handleCopyURL = () => {
        navigator.clipboard.writeText(urlDisplay).then(() => {
            alert(t('urlBuilder.url_copied'));
        });
    };

    const handleAddToken = (token) => {
        if (currentInput) {
            const updatedValue = currentInput.value + token;
            currentInput.onChange({ target: { value: updatedValue } });
        }
        setModalOpen(false);
    };

    return (
        <Box>
            <Header text={t('urlBuilder.header_title')} />
            <Box sx={{ padding: { xs: 2, sm: 10 } }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, marginBottom: 2 }}>
                    <Select value={selectedPlatform} onChange={handlePlatformChange} displayEmpty fullWidth>
                        <MenuItem value="" disabled>{t('urlBuilder.select_platform')}</MenuItem>
                        {platforms.map((platform) => (
                            <MenuItem key={platform.slug} value={platform.slug}>{platform.name}</MenuItem>
                        ))}
                    </Select>
                    <TextField
                        value={baseUrl}
                        onChange={(e) => {
                            const newBaseUrl = e.target.value;
                            setBaseUrl(newBaseUrl);
                            updateURLDisplay(newBaseUrl, parameters);
                        }}
                        placeholder={t('urlBuilder.base_url_placeholder')}
                        fullWidth
                    />
                </Box>
                <Box sx={{ marginBottom: 2 }}>
                    <Typography variant="body1" sx={{ marginBottom: 1 }}>
                        {t('urlBuilder.parameters')}
                    </Typography>
                    {parameters.map((param, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 2, marginBottom: 1 }}>
                            <TextField
                                label={param.name}
                                value={param.value || ''}
                                onChange={(e) => handleParameterChange(index, e.target.value)}
                                fullWidth
                                placeholder={param.highlight === 1 ? t('urlBuilder.highlight_placeholder') : ''}
                                InputProps={{
                                    style: { backgroundColor: param.highlight === 1 ? '#e3f2fd' : 'inherit' },
                                }}
                            />
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setCurrentInput({
                                        value: param.value || '',
                                        onChange: (e) => handleParameterChange(index, e.target.value),
                                    });
                                    setModalOpen(true);
                                }}
                            >
                                +
                            </Button>
                        </Box>
                    ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                        value={urlDisplay}
                        placeholder={t('urlBuilder.url_display_placeholder')}
                        fullWidth
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleCopyURL}>
                                        <FileCopyIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                    <Paper sx={{ padding: 4, height: 600, margin: 'auto', maxWidth: 600, marginTop: '10%' }}>
                        <Typography variant="h5" sx={{ marginBottom: 2, fontWeight: 'bold' }}>
                            {t('urlBuilder.select_token')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <List sx={{ width: '100%', overflowY: 'auto', border: '1px solid #ccc', borderRadius: '4px' }}>
                                <Typography variant="h6" sx={{ marginBottom: 1, marginLeft: 2, fontWeight: 'bold' }}>
                                    {t('urlBuilder.google_ads')}
                                </Typography>
                                <Divider />
                                {[
                                    'keyword', 'adgroupid', 'campaignid', 'creative', 'device',
                                    'extensionid', 'matchtype', 'network', 'gclid'
                                ].map((key, index) => (
                                    <Tooltip key={index} title={t(`urlBuilder.tokens.${key}`)} placement="right">
                                        <ListItem button onClick={() => handleAddToken(`{${key}}`)}>
                                            {`{${key}}`}
                                        </ListItem>
                                    </Tooltip>
                                ))}
                            </List>
                            <List sx={{ width: '100%', overflowY: 'auto', border: '1px solid #ccc', borderRadius: '4px' }}>
                                <Typography variant="h6" sx={{ marginBottom: 1, marginLeft: 2, fontWeight: 'bold' }}>
                                    {t('urlBuilder.microsoft_ads')}
                                </Typography>
                                <Divider />
                                {[
                                    'keyword', 'QueryString', 'AdGroupId', 'AdId', 'CampaignId',
                                    'Device', 'feeditemid', 'MatchType', 'Network', 'msclkid'
                                ].map((key, index) => (
                                    <Tooltip key={index} title={t(`urlBuilder.tokens.${key}`)} placement="right">
                                        <ListItem button onClick={() => handleAddToken(`{${key}}`)}>
                                            {`{${key}}`}
                                        </ListItem>
                                    </Tooltip>
                                ))}
                            </List>
                        </Box>
                    </Paper>
                </Modal>
            </Box>
        </Box>
    );
};

export default URLGenerator;